import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { BasisPoints, MAX_BASIS_POINTS } from '@klear/basis-points'
import { basisPointsFromInput, formatBasisPoints, type BasisPointsInput } from '@klear/basis-points/boundary'
import { Money } from '@klear/money'
import type { InvoiceOfferPaymentMetaWire } from '@klear/invoice-offer/invoice-offer.wire'

export type InvoiceOfferListRow = Pick<
  InvoiceOffer,
  | 'status'
  | 'effectiveDate'
  | 'endDate'
  | 'advanceRate'
  | 'purchasePrice'
  | 'settlementCollection'
  | 'settlementAdvanceRecovery'
  | 'settlementNetAmount'
  | 'currency'
>

function comparableFaceAndAdvance(offer: InvoiceOfferListRow): { face: Money; advance: Money } | null {
  const { settlementCollection: face, purchasePrice: advance } = offer
  if (face.currency !== advance.currency) return null
  return { face, advance }
}

export function invoiceOfferFunderOrganizationId(paymentMeta: InvoiceOfferPaymentMetaWire | undefined): number | null {
  const id = paymentMeta?.klearAccountDetails?.organizationId
  if (id == null || id <= 0) return null
  return id
}

type AdvanceRateInput = BasisPointsInput

export function invoiceOfferAdvanceRateFormatted(offer: { advanceRate: AdvanceRateInput }): string {
  return formatBasisPoints(offer.advanceRate, { digits: 4 })
}

export function isPlausibleInvoiceOfferListMetrics(offer: InvoiceOfferListRow): boolean {
  const amounts = comparableFaceAndAdvance(offer)
  if (!amounts) return false
  const { face, advance } = amounts
  if (!face.isPositive() || !advance.isPositive()) return false
  const expectedAdvance = basisPointsFromInput(offer.advanceRate).applyTo(face)
  const difference = advance.subtract(expectedAdvance)
  const drift = difference.isNegative() ? difference.zero.subtract(difference) : difference
  const tolerance = Money.max(new BasisPoints(1).applyTo(face), face.smallestUnit)
  return drift.lessThanOrEqualTo(tolerance)
}

export function isFullFundingListOffer(offer: InvoiceOfferListRow, minRateBps: number): boolean {
  const amounts = comparableFaceAndAdvance(offer)
  if (!amounts) return false
  const { face, advance } = amounts
  if (!face.isPositive()) return false
  return advance.mul(BigInt(MAX_BASIS_POINTS)).greaterThanOrEqualTo(face.mul(BigInt(minRateBps)))
}
